/**
 * Drop-in replacement for `fetch()` that injects the Sanctum bearer token.
 *
 * Use this for any direct call to /api/* endpoints that doesn't go through
 * simpleApiClient. The Bearer header is added when a token is available;
 * Accept: application/json is set if not provided. `credentials` is left at
 * the caller's value (defaults to 'same-origin', which is fine — no cookies
 * are required under token auth).
 */
import { getToken } from './tokenStore';

export async function apiFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(options.headers);

  const token = getToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  return fetch(url, { ...options, headers });
}

/**
 * Create an EventSource (SSE) with the Sanctum token appended as a query
 * param. EventSource doesn't support custom headers, so the backend's
 * AcceptQueryToken middleware promotes `access_token` to Authorization.
 */
export function apiEventSource(url: string): EventSource {
  const token = getToken();
  if (!token) return new EventSource(url);
  const separator = url.includes('?') ? '&' : '?';
  return new EventSource(`${url}${separator}access_token=${encodeURIComponent(token)}`);
}
